import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import * as authService from "../services/authService";
import { isValidEmail } from "../utils/validators";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);

      const trimmedEmail = email.trim();

      const data = await authService.forgotPassword({
        email: trimmedEmail,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Password reset request failed.");
      }

      navigate("/action-status", {
        replace: true,
        state: {
          status: "email",
          eyebrow: "Reset Link Sent",
          title: "Check your email for password reset instructions",
          message:
            data?.message ||
            "If an account exists for this email address, password reset instructions have been sent.",
          note:
            "Please open your inbox and follow the password reset link. If you do not see the email right away, check your spam or junk folder as well.",
          details: [
            {
              label: "Email Address",
              value: trimmedEmail,
            },
            {
              label: "Next Step",
              value: "Open the email and follow the reset link.",
            },
          ],
          actions: [
            {
              label: "Back to Sign In",
              to: "/login",
              variant: "primary",
            },
            {
              label: "Return Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setError(err?.message || "We could not send reset instructions right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Account Recovery"
            title="Reset your password"
            subtitle="Enter your email address and we’ll send you a secure link to help you regain access to your account."
            className="mb-8"
          />

          {error ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setError("");
              }}
              required
              placeholder="Enter your email address"
            />

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              Send Reset Instructions
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-warm">
            Remembered your password?{" "}
            <Link to="/login" className="font-semibold text-henna hover:underline">
              Return to sign in
            </Link>
          </p>
        </div>
      </PageContainer>
    </div>
  );
};

export default ForgotPassword;